import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../services/api";
import {
  LayoutDashboard,
  Package,
  Truck,
  Users,
  MapPin,
  IndianRupee,
  BarChart3,
  Star,
  LogOut,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  DollarSign,
  Radio,
  Settings,
  Megaphone,
  CalendarClock,
  Wallet,
  Gift,
  Tag,
  UserPlus,
  Bike,
  ShieldAlert,
  LineChart,
  ShoppingBag,
  Zap,
  Package2,
  Activity,
  Ban,
  MessageCircle,
  FileSearch,
  SlidersHorizontal,
  Inbox,
  Globe2,
  ServerCog,
  RotateCcw,
  FileText,
} from "lucide-react";
import { ClipboardList, ClipboardCheck, Warehouse, MapPinned } from "lucide-react";
import { ScrollText } from "lucide-react";
import useAuthStore from "../store/authStore";
import Logo from "../components/Logo";
import ApprovalsSidebarBadge from "./admin/approvals/ApprovalsSidebarBadge";

const HIERARCHY_ROLES = ["founder", "ceo", "main_hub_manager", "micro_hub_lead", "ops_staff"];
const ALL_DASHBOARD_ROLES = ["admin", "user", "agent", ...HIERARCHY_ROLES];

const navItems = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard", roles: ALL_DASHBOARD_ROLES },
  { label: "Orders", icon: Package, path: "/dashboard/orders", roles: ["admin", "user", "agent"] },
  { label: "Tracking", icon: Truck, path: "/dashboard/tracking", roles: ["admin", "user", "agent", "main_hub_manager", "micro_hub_lead"] },
  { label: "Wallet", icon: Wallet, path: "/dashboard/wallet", roles: ["admin", "user"] },
  { label: "Refer & Earn", icon: Gift, path: "/dashboard/referral", roles: ["admin", "user", "agent"] },
  { label: "Loyalty", icon: Star, path: "/dashboard/loyalty", roles: ["user", "admin"] },
  { label: "Leaderboard", icon: BarChart3, path: "/dashboard/leaderboard", roles: ["admin", "user", "agent"] },
  { label: "Help & Support", icon: MessageCircle, path: "/dashboard/help", roles: ["admin", "user", "agent"] },
  { label: "B2B Express", icon: Truck, path: "/dashboard/b2b", roles: ["admin", "user"] },
  { label: "Shopify", icon: ShoppingBag, path: "/dashboard/shopify", roles: ["admin", "user"] },
  { label: "Earnings", icon: IndianRupee, path: "/dashboard/earnings", roles: ["agent", "admin"] },
];

const hierarchyNavItems = [
  { label: "Founder Overview", icon: LineChart, path: "/dashboard/founder", roles: ["founder"], desc: "Read-only board view" },
  { label: "Central Ops", icon: Activity, path: "/dashboard/ceo", roles: ["founder", "ceo", "admin"], desc: "Create / approve Main Hubs" },
  { label: "Manager Accounts", icon: UserPlus, path: "/dashboard/managers", roles: ["founder", "ceo", "admin"], desc: "Create / invite hub manager logins" },
  { label: "Chain of Custody", icon: ScrollText, path: "/dashboard/chain-of-custody", roles: ["founder", "ceo", "admin", "ops_staff", "main_hub_manager"], desc: "Per-order audit reconstruction" },
  { label: "My Main Hub", icon: MapPin, path: "/dashboard/hub", roles: ["main_hub_manager"], desc: "Run your Main Hub" },
  { label: "My Micro Hub", icon: Package2, path: "/dashboard/micro-hub", roles: ["micro_hub_lead"], desc: "Run your Micro Hub" },
  { label: "My Queue", icon: Package, path: "/dashboard/ops", roles: ["ops_staff"], desc: "Today's packages" },
];

const adminNavItems = [
  { label: "Agents", icon: UserCheck, path: "/dashboard/agents", roles: ["admin"] },
  { label: "Customers", icon: Users, path: "/dashboard/customers", roles: ["admin"] },
  { label: "Revenue", icon: DollarSign, path: "/dashboard/revenue", roles: ["admin", "ceo", "founder"] },
  { label: "Live Ops", icon: Radio, path: "/dashboard/live-ops", roles: ["admin", "ceo", "founder"] },
  { label: "Edit Requests", icon: MessageCircle, path: "/dashboard/edit-requests", roles: ["admin"], desc: "Customer change requests" },
  { label: "Analytics", icon: BarChart3, path: "/dashboard/analytics", roles: ["admin", "ceo", "founder"] },
  { label: "Growth Analytics", icon: LineChart, path: "/dashboard/admin/analytics", roles: ["admin", "ceo", "founder"], desc: "Funnel · visits · email health" },
  { label: "Hubs", icon: MapPin, path: "/dashboard/hubs", roles: ["admin", "ceo", "founder"] },
  { label: "Serviceability", icon: MapPin, path: "/dashboard/serviceability", roles: ["admin", "ceo", "founder"], desc: "Pickup service-area + notify-me leads" },
  { label: "Agent Applications", icon: UserPlus, path: "/dashboard/agent-applications", roles: ["admin"] },
  { label: "Gig Commission", icon: Bike, path: "/dashboard/gig-commission", roles: ["admin"] },
  { label: "Damage Claims", icon: ShieldAlert, path: "/dashboard/damage-claims", roles: ["admin"] },
  { label: "Post Job", icon: Radio, path: "/dashboard/target-broadcast", roles: ["admin"] },
  { label: "Welcome Kits", icon: Package2, path: "/dashboard/welcome-kits", roles: ["admin"] },
  { label: "Service Types", icon: Bike, path: "/dashboard/service-types", roles: ["admin"] },
  { label: "Contact Settings", icon: MessageCircle, path: "/dashboard/contact-settings", roles: ["admin"] },
  { label: "Contacts", icon: Megaphone, path: "/dashboard/contacts", roles: ["admin"] },
  { label: "KPIs", icon: LineChart, path: "/dashboard/kpis", roles: ["admin"] },
  { label: "Homepage Stats", icon: BarChart3, path: "/dashboard/stats", roles: ["admin"] },
  { label: "System Health", icon: Activity, path: "/dashboard/system-health", roles: ["admin"], desc: "Backend errors & diagnostics" },
  { label: "Feedback", icon: MessageCircle, path: "/dashboard/feedback", roles: ["admin"], desc: "Customer NPS responses" },
  { label: "Audit Log", icon: FileSearch, path: "/dashboard/audit", roles: ["admin"], desc: "Admin actions trail" },
  { label: "Campaigns", icon: Megaphone, path: "/dashboard/campaigns", roles: ["admin"], desc: "WhatsApp marketing + win-back" },
  { label: "Anomalies", icon: ShieldAlert, path: "/dashboard/anomalies", roles: ["admin"], desc: "Sprint 77 · fraud / abuse flags" },
  { label: "Support Tickets", icon: Inbox, path: "/dashboard/support", roles: ["admin", "founder", "ceo", "support"], desc: "Refunds · complaints · escalations" },
  { label: "Cities", icon: Globe2, path: "/dashboard/cities", roles: ["admin", "founder", "ceo"], desc: "Multi-city operations" },
  { label: "Production Setup", icon: ServerCog, path: "/dashboard/production", roles: ["admin", "founder", "ceo"], desc: "API keys & go-live checklist" },
  { label: "Hub Suggestions", icon: MapPin, path: "/dashboard/hub-suggestions", roles: ["admin"], desc: "Sprint 77 · AI-recommended hubs" },
  { label: "Ops Settings", icon: SlidersHorizontal, path: "/dashboard/site-settings", roles: ["admin"], desc: "Runtime knobs + daily digest" },
  { label: "Broadcast", icon: Megaphone, path: "/dashboard/broadcast", roles: ["admin"] },
  { label: "Reports", icon: CalendarClock, path: "/dashboard/reports", roles: ["admin"] },
  { label: "Payouts", icon: Wallet, path: "/dashboard/payouts", roles: ["admin"] },
  { label: "Refund Lifecycle", icon: RotateCcw, path: "/dashboard/refunds", roles: ["admin"], desc: "Sprint 81 · cancellation & edit refunds queue" },
  { label: "Status Report (PDF)", icon: FileText, path: "__download_status_report__", roles: ["admin", "founder", "ceo"], desc: "Download platform & production readiness report", isAction: true },
];

const pricingNavItems = [
  { label: "Pricing",      icon: Settings,     path: "/dashboard/pricing",      roles: ["admin"], desc: "PickNGo Direct rates" },
  { label: "Pricing Health", icon: Activity,   path: "/dashboard/pricing-health", roles: ["admin"], desc: "Margin sanity checker" },
  { label: "Coupons",      icon: Tag,          path: "/dashboard/coupons",      roles: ["admin"], desc: "Promo codes & discounts" },
  { label: "Cancellations", icon: Ban,         path: "/dashboard/cancellations", roles: ["admin"], desc: "Cancellation audit log" },
  { label: "Couriers",     icon: Truck,        path: "/dashboard/couriers",     roles: ["admin"], desc: "External partner tariffs" },
  { label: "Hub Pricing",  icon: IndianRupee,  path: "/dashboard/hub-pricing",  roles: ["admin"], desc: "Internal hub costs" },
  { label: "B2B Pricing",  icon: Tag,          path: "/dashboard/b2b-pricing",  roles: ["admin"], desc: "Business contract rates" },
  { label: "B2B Contracts", icon: FileText,    path: "/dashboard/b2b-contracts", roles: ["admin", "city_manager"], desc: "Contract pricing & approvals" },
  { label: "Surge",        icon: Zap,          path: "/dashboard/surge",        roles: ["admin"], desc: "Time-based multiplier" },
];

const agentNavItems = [
  { label: "Live Job Feed", icon: Zap, path: "/dashboard/dispatch", roles: ["agent"] },
  { label: "My Tasks", icon: ClipboardList, path: "/dashboard/agent/tasks", roles: ["agent"], desc: "Phase-104 · Active assignments" },
  { label: "My Earnings", icon: IndianRupee, path: "/dashboard/agent/earnings", roles: ["agent"], desc: "Phase-105B · COD + payouts ledger" },
  { label: "Hub Orders", icon: Warehouse, path: "/dashboard/micro-hub/orders", roles: ["micro_hub_lead"], desc: "Phase MHM-5.4 · Micro hub queue" },
  { label: "Hub Orders", icon: Warehouse, path: "/dashboard/microhub/orders", roles: ["ops_staff"], desc: "Phase-104 · Micro hub queue" },
  { label: "Region Orders", icon: MapPinned, path: "/dashboard/hub/orders", roles: ["main_hub_manager"], desc: "Phase-104 · Region queue" },
  { label: "Approvals", icon: ClipboardCheck, path: "/dashboard/admin/approvals", roles: ["admin"], desc: "Phase APP-1 · Micro hub & packer approvals" },
  { label: "College Pricing", icon: ClipboardCheck, path: "/dashboard/admin/college-pricing", roles: ["admin"], desc: "Phase COLLEGE-PRICING-1 · CMS" },
  { label: "Oversight", icon: ShieldAlert, path: "/dashboard/founder/oversight", roles: ["founder", "ceo"], desc: "Phase APP-3 · Company-wide approvals + override" },
  { label: "College Pricing", icon: ClipboardCheck, path: "/dashboard/founder/college-pricing", roles: ["founder"], desc: "Phase COLLEGE-PRICING-1 · CMS" },
  { label: "Approvals", icon: ClipboardCheck, path: "/dashboard/approvals", roles: ["founder", "ceo"], desc: "Phase-103 · Pending decisions" },
];

export default function Sidebar({ collapsed, onToggle, mobile = false, onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const role = user?.role || "user";

  // Sprint 77 — operational alert badge for admins
  const [alerts, setAlerts] = useState({ anomaliesOpen: 0, anomaliesHigh: 0 });
  useEffect(() => {
    if (!["admin", "founder", "ceo"].includes(role)) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/anomalies/summary");
        if (!alive) return;
        setAlerts({
          anomaliesOpen: data?.by_status?.open || 0,
          anomaliesHigh: data?.by_severity?.high || 0,
        });
      } catch {/* silent */}
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, [role]);

  const badgeForPath = (path) => {
    if (path !== "/dashboard/anomalies") return null;
    if (!alerts.anomaliesOpen) return null;
    return {
      count: alerts.anomaliesOpen,
      tone: alerts.anomaliesHigh > 0 ? "high" : "normal",
    };
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(path);
  };

  const handleNavClick = () => {
    if (mobile && typeof onNavigate === "function") onNavigate();
  };

  // Sprint 81+ — Direct PDF download for the Status Report. The endpoint
  // requires a Bearer token so we fetch via axios (which attaches the
  // header) and trigger a browser save via a Blob URL.
  const downloadStatusReport = async () => {
    try {
      const { default: api } = await import("../services/api");
      const res = await api.get("/admin/status-report.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "PickNGo_Platform_Status_Report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e?.response?.data?.detail || "Could not download status report");
    }
  };

  const filteredNav = navItems.filter((item) => item.roles.includes(role));
  const filteredHierarchy = hierarchyNavItems.filter((item) => item.roles.includes(role));
  const filteredAdmin = adminNavItems.filter((item) => item.roles.includes(role));
  const filteredAgent = agentNavItems.filter((item) => item.roles.includes(role));
  const filteredPricing = pricingNavItems.filter((item) => item.roles.includes(role));

  // Mobile mode: render inside the drawer wrapper (relative + full width), always visible.
  // Desktop mode: fixed sidebar, hidden below md breakpoint.
  const rootClass = mobile
    ? "w-full bg-white h-full flex flex-col"
    : `${collapsed ? "w-[72px]" : "w-64"} bg-white border-r border-slate-200 h-screen fixed top-0 left-0 flex-col hidden md:flex transition-all duration-300 z-40`;

  return (
    <aside
      className={rootClass}
      data-testid={mobile ? "dashboard-sidebar-mobile" : "dashboard-sidebar"}
    >
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-slate-100 justify-between">
        <Link to="/" className="flex items-center gap-2.5 no-underline" data-testid="sidebar-logo">
          <div className="w-9 h-9 bg-[#FF6B00] rounded-xl flex items-center justify-center shrink-0">
            <Package className="h-5 w-5 text-white" />
          </div>
          {!collapsed && <span className="font-['Outfit'] text-lg font-extrabold tracking-tight"><span className="text-slate-900">Pick</span><span className="text-[#FF6B00]">N</span><span className="text-slate-900">Go</span></span>}
        </Link>
        <button
          onClick={onToggle}
          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>

      {/* Nav */}
      <div className="flex-1 py-4 px-3 flex flex-col gap-1 overflow-y-auto">
        <p className={`text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-2 ${collapsed ? "text-center" : "px-3"}`}>
          {collapsed ? "..." : "Menu"}
        </p>
        {filteredNav.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={handleNavClick}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 no-underline ${
              isActive(item.path)
                ? "bg-orange-50 text-[#FF6B00]"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            } ${collapsed ? "justify-center" : ""}`}
            data-testid={`sidebar-nav-${item.label.toLowerCase()}`}
            title={collapsed ? item.label : undefined}
          >
            <item.icon className="h-5 w-5 shrink-0" />
            {!collapsed && item.label}
          </Link>
        ))}

        {filteredAgent.length > 0 && (
          <>
            <div className="my-3 border-t border-slate-100" />
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-2 ${collapsed ? "text-center" : "px-3"}`}>
              {collapsed ? "..." : "Agent"}
            </p>
            {filteredAgent.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 no-underline ${
                  isActive(item.path)
                    ? "bg-orange-50 text-[#FF6B00]"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                } ${collapsed ? "justify-center" : ""}`}
                data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                title={collapsed ? item.label : undefined}
              >
                <item.icon className="h-5 w-5 shrink-0" />
                {!collapsed && item.label}
                {(item.path === "/dashboard/admin/approvals" || item.path === "/dashboard/founder/oversight") && !collapsed && (
                  <ApprovalsSidebarBadge />
                )}
              </Link>
            ))}
          </>
        )}

        {filteredHierarchy.length > 0 && (
          <>
            <div className="my-3 border-t border-slate-100" />
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-2 ${collapsed ? "text-center" : "px-3"}`}>
              {collapsed ? "..." : "Operations"}
            </p>
            {filteredHierarchy.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 no-underline ${
                  isActive(item.path)
                    ? "bg-orange-50 text-[#FF6B00]"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                } ${collapsed ? "justify-center" : ""}`}
                data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                title={collapsed ? item.label : item.desc || item.label}
              >
                <item.icon className="h-5 w-5 shrink-0" />
                {!collapsed && item.label}
                {(item.path === "/dashboard/admin/approvals" || item.path === "/dashboard/founder/oversight") && !collapsed && (
                  <ApprovalsSidebarBadge />
                )}
              </Link>
            ))}
          </>
        )}

        {filteredAdmin.length > 0 && (
          <>
            <div className="my-3 border-t border-slate-100" />
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-2 ${collapsed ? "text-center" : "px-3"}`}>
              {collapsed ? "..." : "Admin"}
            </p>
            {filteredAdmin.map((item) => {
              const badge = badgeForPath(item.path);
              // Sprint 81+ — Status Report is an action, not a route.
              if (item.isAction && item.path === "__download_status_report__") {
                return (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => { handleNavClick(); downloadStatusReport(); }}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 w-full text-left text-slate-600 hover:bg-slate-50 hover:text-slate-900 ${collapsed ? "justify-center" : ""}`}
                    data-testid="sidebar-download-status-report"
                    title={collapsed ? item.label : undefined}
                  >
                    <span className="relative shrink-0">
                      <item.icon className="h-5 w-5" />
                    </span>
                    {!collapsed && <span className="flex-1">{item.label}</span>}
                    {!collapsed && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#FF6B00] bg-orange-50 px-1.5 py-0.5 rounded">
                        PDF
                      </span>
                    )}
                  </button>
                );
              }
              return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 no-underline ${
                  isActive(item.path)
                    ? "bg-orange-50 text-[#FF6B00]"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                } ${collapsed ? "justify-center" : ""}`}
                data-testid={`sidebar-nav-${item.label.toLowerCase().replace(" ", "-")}`}
                title={collapsed ? `${item.label}${badge ? ` · ${badge.count} open` : ""}` : undefined}
              >
                <span className="relative shrink-0">
                  <item.icon className="h-5 w-5" />
                  {badge && collapsed && (
                    <span
                      className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full ring-2 ring-white ${badge.tone === "high" ? "bg-rose-500 animate-pulse" : "bg-amber-500"}`}
                      data-testid="sidebar-anomaly-dot"
                    />
                  )}
                </span>
                {!collapsed && <span className="flex-1">{item.label}</span>}
                {!collapsed && badge && (
                  <span
                    className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${badge.tone === "high" ? "bg-rose-500 text-white animate-pulse" : "bg-amber-500 text-white"}`}
                    data-testid="sidebar-anomaly-badge"
                  >
                    {badge.count > 99 ? "99+" : badge.count}
                  </span>
                )}
              </Link>
            );})}
          </>
        )}

        {filteredPricing.length > 0 && (
          <>
            <div className="my-3 border-t border-slate-100" />
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-2 ${collapsed ? "text-center" : "px-3"}`} data-testid="sidebar-section-pricing">
              {collapsed ? "..." : "Pricing"}
            </p>
            {filteredPricing.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`flex items-start gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 no-underline ${
                  isActive(item.path)
                    ? "bg-orange-50 text-[#FF6B00]"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                } ${collapsed ? "justify-center" : ""}`}
                data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                title={collapsed ? `${item.label} — ${item.desc}` : undefined}
              >
                <item.icon className="h-5 w-5 shrink-0 mt-0.5" />
                {!collapsed && (
                  <span className="flex-1 min-w-0 leading-tight">
                    <span className="block">{item.label}</span>
                    <span className="block text-[10.5px] text-slate-400 font-normal mt-0.5 truncate" data-testid={`sidebar-desc-${item.label.toLowerCase().replace(/ /g, "-")}`}>
                      {item.desc}
                    </span>
                  </span>
                )}
              </Link>
            ))}
          </>
        )}
      </div>

      {/* User + Logout */}
      <div className="border-t border-slate-100 p-3">
        {!collapsed && (
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
              <span className="text-[#FF6B00] text-sm font-semibold font-['Outfit']">
                {(user?.name || "U")[0].toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name || "User"}</p>
              <p className="text-xs text-slate-400 capitalize">{role}</p>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-all w-full ${collapsed ? "justify-center" : ""}`}
          data-testid="sidebar-logout-btn"
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {!collapsed && "Logout"}
        </button>
      </div>
    </aside>
  );
}
